import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import { useBoolean } from 'usehooks-ts';
import { Card, Container } from 'reactstrap';

import Breadcrumb from 'components/App/Breadcrumb';
import Button from 'components/App/Button';
import DataTableManagerPromotion from 'components/App/DataTableManagerPromotion';
import Loading from 'components/App/Loading';
import Meta from 'components/App/Meta';
import Pagination from 'components/Pagination';
import Selection from 'components/App/Selection';
import ModalCreateMarketingPromotion from 'components/Modals/ModalCreateMarketingPromotion';
import ModalRequestAction from 'components/Modals/ModalRequestAction';
import { LOCATIONS } from 'constants/index';
import { MARKETING_PROMOTION_STATUS_FILTER_OPTIONS } from 'constants/promotion';
import cn from 'utils/classnames';
import { getParamsFormObject } from 'utils';
import totalPage from 'utils/totalPage';
import images from 'assets/images';

import styles from './ManagerPromotion.module.scss';

const PAGE_TITLE = 'QUẢN LÝ KHUYẾN MÃI';
const SECTION_TITLE = 'Danh sách Ưu đãi Tiếp thị';
const CURRENT_YEAR = new Date().getFullYear();
const DEFAULT_LIMIT = 10;

const breadcrumbList = [
  {
    name: 'Quản lý khuyến mãi',
    isActive: true,
    link: LOCATIONS.MANAGER_PROMOTION,
  },
];

const buildYearOptions = () => {
  const startYear = CURRENT_YEAR - 2;
  const endYear = CURRENT_YEAR + 2;

  return Array.from({ length: endYear - startYear + 1 }, (_, index) => {
    const year = startYear + index;
    return {
      value: year,
      label: String(year),
    };
  });
};

const buildSearchParams = ({ page, year, status }) => {
  const params = {
    page,
    year,
  };

  if (status) {
    params.status = status;
  }

  return getParamsFormObject(params);
};

function ManagerPromotion({
  isLoadingGetMarketingPromotionList,
  isLoadingCreateMarketingPromotion,
  isLoadingUpdateMarketingPromotion,
  isLoadingGetMarketingPromotionDetail,
  isLoadingDeleteMarketingPromotion,
  marketingPromotionList,
  marketingPromotionTotal,
  marketingPromotionDetail,
  actionGetMarketingPromotionList,
  actionGetMarketingPromotionDetail,
  actionCreateMarketingPromotion,
  actionUpdateMarketingPromotion,
  actionDeleteMarketingPromotion,
}) {
  const history = useHistory();
  const [isOpenCreateModal, setIsOpenCreateModal] = useState(false);
  const [editingPromotionId, setEditingPromotionId] = useState('');
  const [deletingPromotionId, setDeletingPromotionId] = useState('');

  const {
    value: isOpenDeleteModal,
    setTrue: onOpenDeleteModal,
    setFalse: onCloseDeleteModal,
  } = useBoolean(false);

  const searchQueries = useMemo(() => {
    const params = new URLSearchParams(history.location.search);

    return {
      page: Number(params.get('page')) || 1,
      year: Number(params.get('year')) || CURRENT_YEAR,
      status: params.get('status') || '',
    };
  }, [history.location.search]);

  const yearOptions = useMemo(() => buildYearOptions(), []);

  const pageCount = useMemo(
    () => totalPage(marketingPromotionTotal, DEFAULT_LIMIT),
    [marketingPromotionTotal],
  );

  useEffect(() => {
    actionGetMarketingPromotionList({
      page: searchQueries.page,
      limit: DEFAULT_LIMIT,
      year: searchQueries.year,
      status: searchQueries.status || undefined,
    });
  }, [actionGetMarketingPromotionList, searchQueries]);

  const pushSearchQueries = useCallback(
    (updates) => {
      history.push({
        pathname: LOCATIONS.MANAGER_PROMOTION,
        search: buildSearchParams({
          page: updates.page ?? searchQueries.page,
          year: updates.year ?? searchQueries.year,
          status: updates.status ?? searchQueries.status,
        }),
      });
    },
    [history, searchQueries],
  );

  const onChangeYear = useCallback(
    (option) => {
      pushSearchQueries({
        page: 1,
        year: option?.value ?? CURRENT_YEAR,
      });
    },
    [pushSearchQueries],
  );

  const onChangeStatus = useCallback(
    (option) => {
      pushSearchQueries({
        page: 1,
        status: option?.value ?? '',
      });
    },
    [pushSearchQueries],
  );

  const onChangePage = useCallback(
    (selected) => {
      pushSearchQueries({
        page: selected.selected + 1,
      });
    },
    [pushSearchQueries],
  );

  const onCreatePromotion = useCallback(() => {
    setIsOpenCreateModal(true);
  }, []);

  const onOpenEditPromotion = useCallback((promotion) => {
    const promotionId = promotion?._id;
    if (!promotionId) return;

    setEditingPromotionId(promotionId);
    setIsOpenCreateModal(true);
    actionGetMarketingPromotionDetail({ id: promotionId });
  }, [actionGetMarketingPromotionDetail]);

  const onCloseCreateModal = useCallback(() => {
    setIsOpenCreateModal(false);
    setEditingPromotionId('');
  }, []);

  const reloadMarketingPromotionList = useCallback(() => {
    actionGetMarketingPromotionList({
      page: searchQueries.page,
      limit: DEFAULT_LIMIT,
      year: searchQueries.year,
      status: searchQueries.status || undefined,
    });
  }, [
    actionGetMarketingPromotionList,
    searchQueries.page,
    searchQueries.status,
    searchQueries.year,
  ]);

  const onDeletePromotion = useCallback((promotion) => {
    const promotionId = promotion?._id;
    if (!promotionId) return;

    setDeletingPromotionId(promotionId);
    onOpenDeleteModal();
  }, [onOpenDeleteModal]);

  const onConfirmDeletePromotion = useCallback(() => {
    if (!deletingPromotionId) return;

    actionDeleteMarketingPromotion({
      id: deletingPromotionId,
      callback: () => {
        onCloseDeleteModal();
        setDeletingPromotionId('');
        reloadMarketingPromotionList();
      },
    });
  }, [
    actionDeleteMarketingPromotion,
    deletingPromotionId,
    onCloseDeleteModal,
    reloadMarketingPromotionList,
  ]);

  const onCreateMarketingPromotion = useCallback(
    (values) => {
      actionCreateMarketingPromotion({
        values,
        callback: () => {
          onCloseCreateModal();
          reloadMarketingPromotionList();
        },
      });
    },
    [
      actionCreateMarketingPromotion,
      onCloseCreateModal,
      reloadMarketingPromotionList,
    ],
  );

  const onUpdateMarketingPromotion = useCallback(
    (values) => {
      if (!editingPromotionId) return;

      actionUpdateMarketingPromotion({
        id: editingPromotionId,
        values,
        callback: () => {
          onCloseCreateModal();
          reloadMarketingPromotionList();
        },
      });
    },
    [
      actionUpdateMarketingPromotion,
      editingPromotionId,
      onCloseCreateModal,
      reloadMarketingPromotionList,
    ],
  );

  return (
    <div className={cn('page-content', styles.Page)}>
      <Meta title={PAGE_TITLE} />

      <Loading loading={isLoadingGetMarketingPromotionList} loaderOnPage />

      <Container fluid>
        <Breadcrumb pageTitle={PAGE_TITLE} breadcrumbList={breadcrumbList} />

        <Card className="Section">
          <div className={styles.SectionHeader}>
            <h3 className={styles.SectionTitle}>{SECTION_TITLE}</h3>

            <div className={styles.SectionActions}>
              <div className={cn(styles.FilterSelect, styles.StatusSelect)}>
                <Selection
                  options={MARKETING_PROMOTION_STATUS_FILTER_OPTIONS}
                  value={searchQueries.status}
                  onChange={onChangeStatus}
                  selectedClassName={styles.FilterSelectControl}
                />
              </div>

              <div className={cn(styles.FilterSelect, styles.YearSelect)}>
                <Selection
                  options={yearOptions}
                  value={searchQueries.year}
                  onChange={onChangeYear}
                  selectedClassName={styles.FilterSelectControl}
                />
              </div>

              <Button
                classes="Button__Add"
                fontSize="13px"
                type="button"
                onClick={onCreatePromotion}
              >
                <i className="fas fa-plus" />
                Tạo ưu đãi tiếp thị
              </Button>
            </div>
          </div>

          <div className={styles.TableWrapper}>
            <DataTableManagerPromotion
              promotions={marketingPromotionList}
              onEdit={onOpenEditPromotion}
              onDelete={onDeletePromotion}
            />
          </div>
        </Card>

        {pageCount > 1 && (
          <Pagination
            page={searchQueries.page}
            pageCount={pageCount}
            onChangePage={onChangePage}
          />
        )}
      </Container>

      <ModalCreateMarketingPromotion
        isOpen={isOpenCreateModal}
        isLoadingDetail={isLoadingGetMarketingPromotionDetail}
        initialValues={marketingPromotionDetail}
        editingId={editingPromotionId}
        onCloseModal={onCloseCreateModal}
        onConfirm={editingPromotionId ? onUpdateMarketingPromotion : onCreateMarketingPromotion}
        isLoadingConfirm={
          isLoadingCreateMarketingPromotion || isLoadingUpdateMarketingPromotion
        }
      />

      <ModalRequestAction
        isOpen={isOpenDeleteModal}
        icon={images.modalGarbageCan.default}
        textRequest={'Bạn có chắc chắn muốn xóa \n ưu đãi tiếp thị này không?'}
        onCloseModal={onCloseDeleteModal}
        onConfirm={onConfirmDeletePromotion}
        isLoadingConfirm={isLoadingDeleteMarketingPromotion}
      />
    </div>
  );
}

ManagerPromotion.propTypes = {
  isLoadingGetMarketingPromotionList: PropTypes.bool,
  isLoadingCreateMarketingPromotion: PropTypes.bool,
  isLoadingUpdateMarketingPromotion: PropTypes.bool,
  isLoadingGetMarketingPromotionDetail: PropTypes.bool,
  isLoadingDeleteMarketingPromotion: PropTypes.bool,
  marketingPromotionList: PropTypes.arrayOf(PropTypes.shape({})),
  marketingPromotionTotal: PropTypes.number,
  marketingPromotionDetail: PropTypes.shape({}),
  actionGetMarketingPromotionList: PropTypes.func.isRequired,
  actionGetMarketingPromotionDetail: PropTypes.func.isRequired,
  actionCreateMarketingPromotion: PropTypes.func.isRequired,
  actionUpdateMarketingPromotion: PropTypes.func.isRequired,
  actionDeleteMarketingPromotion: PropTypes.func.isRequired,
};

ManagerPromotion.defaultProps = {
  isLoadingGetMarketingPromotionList: false,
  isLoadingCreateMarketingPromotion: false,
  isLoadingUpdateMarketingPromotion: false,
  isLoadingGetMarketingPromotionDetail: false,
  isLoadingDeleteMarketingPromotion: false,
  marketingPromotionList: [],
  marketingPromotionTotal: 0,
  marketingPromotionDetail: null,
};

export default ManagerPromotion;
